/**
 * Element-library management page (Block 5). Its own area, independent of the
 * (stilled) Excalidraw library panel. Browse folders + items, create folders,
 * move items by drag & drop, and see a client-side preview rendered from the
 * item's elements (no thumbnail pipeline — Block 5.4).
 */

import { useCallback, useEffect, useState } from "react";

import { ApiError, ryfeApi } from "../api";
import { useRyfe } from "../RyfeContext";
import { navigate } from "../router";
import { sortRows, useTableSort } from "../table-sort";

import { LibraryPreview } from "./LibraryPreview";
import { SortableTh } from "./SortableTh";

import "./ryfe.scss";

import type { LibraryFolderRow, LibraryItemRow, LibraryListing } from "../api";

export const RyfeLibrary = ({ folderId }: { folderId?: string }) => {
  const { workspace } = useRyfe();
  const wsId = workspace?.id;
  const [listing, setListing] = useState<LibraryListing | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState<string | null>(null);
  // v1.3.0 Block 2 (D-17) — two views: the existing table and a preview grid
  // with the name UNDER the element.
  const [view, setView] = useState<"list" | "grid">("list");

  const load = useCallback(async () => {
    if (!wsId) {
      return;
    }
    setError(null);
    try {
      setListing(await ryfeApi.getLibrary(wsId, folderId || undefined));
    } catch (e) {
      setError((e as Error).message);
    }
  }, [wsId, folderId]);

  useEffect(() => {
    load();
  }, [load]);

  const canWrite = !!listing?.can_write;

  // CC_05 Block 4 — Spalten-Sortierung für die Bibliothekstabelle.
  const { sort, toggle, indicator } = useTableSort();
  const itemVal = (it: LibraryItemRow, key: string): unknown => {
    switch (key) {
      case "name":
        return it.name;
      case "count":
        return it.element_count;
      case "source":
        return it.source_package;
      case "created":
        return it.created_at;
      case "license":
        return it.license;
      default:
        return null;
    }
  };
  const sortedItems = listing ? sortRows(listing.items, sort, itemVal) : [];

  const newFolder = async () => {
    if (!wsId) {
      return;
    }
    const name = window.prompt("Name des Bibliotheks-Ordners");
    if (!name) {
      return;
    }
    try {
      await ryfeApi.createLibraryFolder(wsId, {
        name,
        parentId: folderId || null,
      });
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Anlegen fehlgeschlagen");
    }
  };

  const moveItemTo = async (itemId: string, destFolderId: string) => {
    if (!wsId) {
      return;
    }
    setDragOver(null);
    try {
      await ryfeApi.patchLibraryItem(wsId, itemId, { folderId: destFolderId });
      await load();
    } catch (e) {
      setError(
        e instanceof ApiError ? e.message : "Verschieben fehlgeschlagen",
      );
    }
  };

  const folderDrop = (f: LibraryFolderRow) => ({
    onDragOver: (e: React.DragEvent) => {
      if (canWrite) {
        e.preventDefault();
        setDragOver(f.id);
      }
    },
    onDragLeave: () => setDragOver((d) => (d === f.id ? null : d)),
    onDrop: (e: React.DragEvent) => {
      e.preventDefault();
      const itemId = e.dataTransfer.getData("application/x-ryfe-item");
      if (itemId) {
        moveItemTo(itemId, f.id);
      }
    },
  });

  return (
    <div className="ryfe-page">
      <div className="ryfe-toolbar">
        <nav className="ryfe-breadcrumb" aria-label="Breadcrumb">
          <button
            className="ryfe-btn ryfe-btn--link"
            onClick={() => navigate("/library")}
          >
            Bibliothek
          </button>
          {folderId && listing?.name && (
            <span>
              {" / "}
              <span className="ryfe-breadcrumb__current">{listing.name}</span>
            </span>
          )}
        </nav>
        <span className="ryfe-topbar__spacer" />
        <button
          className="ryfe-btn ryfe-btn--ghost"
          onClick={() => setView((v) => (v === "list" ? "grid" : "list"))}
          title="Ansicht wechseln"
        >
          {view === "list" ? "▦ Raster" : "☰ Liste"}
        </button>
        <button
          className="ryfe-btn ryfe-btn--ghost"
          onClick={newFolder}
          disabled={!canWrite}
        >
          Neuer Ordner
        </button>
      </div>

      <p className="ryfe-muted">
        Elemente fügst du in ein offenes Board über die Schaltfläche
        „Bibliothek“ im Board ein. Ziehe Einträge auf einen Ordner, um sie
        einzusortieren.
      </p>

      {error && <div className="ryfe-auth__error">{error}</div>}

      {!listing ? (
        <div className="ryfe-muted">Loading…</div>
      ) : view === "grid" ? (
        <div className="ryfe-libgrid ryfe-libgrid--page">
          {listing.folders.map((f) => (
            <button
              key={f.id}
              className="ryfe-picker__row"
              onClick={() => navigate(`/library/${f.id}`)}
            >
              <span className="ryfe-libgrid__art">📁</span>
              <span>{f.name}</span>
            </button>
          ))}
          {listing.items.map((it: LibraryItemRow) => (
            <div key={it.id} className="ryfe-picker__row">
              <span className="ryfe-libgrid__art">
                {wsId && <LibraryPreview wsId={wsId} itemId={it.id} />}
              </span>
              <span>{it.name}</span>
            </div>
          ))}
          {listing.folders.length === 0 && listing.items.length === 0 && (
            <div className="ryfe-muted">Leer.</div>
          )}
        </div>
      ) : (
        <div className="ryfe-table-wrap">
          <table className="ryfe-table ryfe-table--library">
            <thead>
              <tr>
                <SortableTh
                  sortKey="name"
                  label="Name"
                  toggle={toggle}
                  indicator={indicator}
                />
                <th>Vorschau</th>
                <th>Kategorie / Ordner</th>
                <SortableTh
                  sortKey="count"
                  label="Anzahl Elemente"
                  toggle={toggle}
                  indicator={indicator}
                />
                <SortableTh
                  sortKey="source"
                  label="Quelle (Paket)"
                  toggle={toggle}
                  indicator={indicator}
                />
                <SortableTh
                  sortKey="created"
                  label="Hinzugefügt"
                  toggle={toggle}
                  indicator={indicator}
                />
                <SortableTh
                  sortKey="license"
                  label="Lizenz"
                  toggle={toggle}
                  indicator={indicator}
                />
              </tr>
            </thead>
            <tbody>
              {listing.folders.map((f) => (
                <tr
                  key={f.id}
                  className={`ryfe-row${
                    dragOver === f.id ? " ryfe-row--drop" : ""
                  }`}
                  {...folderDrop(f)}
                >
                  <td>
                    <button
                      className="ryfe-linkish"
                      onClick={() => navigate(`/library/${f.id}`)}
                    >
                      📁 {f.name}
                    </button>
                  </td>
                  <td>—</td>
                  <td>Ordner</td>
                  <td>{f.item_count ?? 0}</td>
                  <td>—</td>
                  <td>—</td>
                  <td>—</td>
                </tr>
              ))}
              {sortedItems.map((it: LibraryItemRow) => (
                <tr
                  key={it.id}
                  className="ryfe-row"
                  draggable
                  onDragStart={(e) =>
                    e.dataTransfer.setData("application/x-ryfe-item", it.id)
                  }
                >
                  <td>{it.name}</td>
                  <td>
                    {wsId && <LibraryPreview wsId={wsId} itemId={it.id} />}
                  </td>
                  <td>{listing.name || "—"}</td>
                  <td>{it.element_count}</td>
                  <td>{it.source_package || "—"}</td>
                  <td>
                    {it.created_at
                      ? new Date(it.created_at).toLocaleDateString()
                      : "—"}
                  </td>
                  <td>{it.license || "—"}</td>
                </tr>
              ))}
              {listing.folders.length === 0 && listing.items.length === 0 && (
                <tr>
                  <td colSpan={7} className="ryfe-muted">
                    Leer.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
